/**
 * The axis editor for a curated global.
 *
 * The **parts** are the design system's — `AxisRow`, `OptionTag`, `NewOptionTag`,
 * `AddOptionButton`, the same ones the Studio composes — so the control looks and behaves
 * identically in both apps. What is not shared is the write: the Studio patches
 * `authors/{uid}/primitives/{id}` and this patches `primitives/{id}`, and hoisting the panel
 * would have meant hoisting a write path into a package that imports no firebase.
 *
 * **Axes are not identity.** Editing them never invalidates the base reference and never
 * bumps the version (PRD 02 §6) — the line under the panel says so, because everyone assumes
 * otherwise the first time. Removing an option keeps the variants already drawn for it.
 *
 * The guardrail is the engine's: `applyPrimitivePatch` throws `AxesTooLargeError` before any
 * network call, so a refused edit costs nothing.
 */

import {
  AddOptionButton,
  AxisRow,
  NewOptionTag,
  OptionTag,
  RowMenu,
} from '@atlas-nook/design-system';
import { AxesTooLargeError } from '@atlas-nook/story';
import type { AxisDef, Primitive } from '@atlas-nook/story';
import { useState } from 'react';
import type { JSX } from 'react';

import { patchGlobal } from '../data/writes.js';
import { ADMIN_STRINGS as S } from '../strings.js';

/** The sentinel for "the new-axis chip is open", so one piece of state covers both rows. */
const NEW_AXIS = '__axis';

/** A key from a label: lower-case, spaces to dashes. The label is what a parent sees. */
function keyFor(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function GlobalAxes({ primitive }: { primitive: Primitive }): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  async function write(axes: AxisDef[]): Promise<boolean> {
    try {
      await patchGlobal(primitive, { axes });
      setError(null);
      return true;
    } catch (failure) {
      setError(failure instanceof AxesTooLargeError ? S.global.axesTooBig : S.common.failed);
      return false;
    }
  }

  async function addOption(axis: AxisDef, label: string): Promise<void> {
    setAdding(null);
    if (label.trim() === '') return;

    const key = keyFor(label);
    if (axis.options.some((option) => option.key === key)) return;

    await write(
      primitive.axes.map((candidate) =>
        candidate.key === axis.key
          ? { ...candidate, options: [...candidate.options, { key, label: { en: label.trim() } }] }
          : candidate,
      ),
    );
  }

  async function addAxis(label: string): Promise<void> {
    setAdding(null);
    if (label.trim() === '') return;

    const key = keyFor(label);
    if (primitive.axes.some((axis) => axis.key === key)) return;
    await write([...primitive.axes, { key, label: { en: label.trim() }, options: [] }]);
  }

  return (
    <div>
      {primitive.axes.length === 0 ? (
        <p className="note" style={{ marginTop: 'var(--sp-3)' }}>
          {S.global.axesNone}
        </p>
      ) : (
        primitive.axes.map((axis) => (
          <AxisRow
            key={axis.key}
            name={axis.key}
            rowLabel={S.global.optionsCount(axis.options.length)}
            count={S.global.optionsCount(axis.options.length)}
            menu={
              <RowMenu
                label={S.global.removeAxis(axis.key)}
                items={[
                  {
                    key: 'remove',
                    label: S.global.removeAxis(axis.key),
                    tone: 'remove',
                    onSelect: () =>
                      void write(primitive.axes.filter((candidate) => candidate.key !== axis.key)),
                  },
                ]}
              />
            }
            add={
              adding === axis.key ? (
                <NewOptionTag
                  label={S.global.newOption(axis.key)}
                  onCommit={(label) => void addOption(axis, label)}
                  onCancel={() => setAdding(null)}
                />
              ) : (
                <AddOptionButton onClick={() => setAdding(axis.key)}>
                  {S.global.addOption}
                </AddOptionButton>
              )
            }
          >
            {axis.options.map((option) => (
              <OptionTag
                key={option.key}
                id={option.key}
                removeLabel={S.global.removeOption(axis.key, option.label.en)}
                onRemove={() =>
                  void write(
                    primitive.axes.map((candidate) =>
                      candidate.key === axis.key
                        ? {
                            ...candidate,
                            options: candidate.options.filter((entry) => entry.key !== option.key),
                          }
                        : candidate,
                    ),
                  )
                }
              >
                {option.label.en}
              </OptionTag>
            ))}
          </AxisRow>
        ))
      )}

      <div className="btnrow" style={{ marginTop: 'var(--sp-3)' }}>
        {adding === NEW_AXIS ? (
          <NewOptionTag
            label={S.global.newAxis}
            onCommit={(label) => void addAxis(label)}
            onCancel={() => setAdding(null)}
          />
        ) : (
          <AddOptionButton onClick={() => setAdding(NEW_AXIS)}>{S.global.addAxis}</AddOptionButton>
        )}
      </div>

      {error !== null ? (
        <p className="note" style={{ color: 'var(--danger)' }} role="alert">
          {error}
        </p>
      ) : (
        <p className="note">{S.global.axesHint}</p>
      )}
      <p className="cap">{S.global.axesBound}</p>
    </div>
  );
}
